import { createSelector } from 'reselect';

import { AUTHENTICATION_CORE_MODULE_NAME } from '../../constants';
import { State } from '../reducer';

const getState = (state: { [AUTHENTICATION_CORE_MODULE_NAME]: State }) => state[AUTHENTICATION_CORE_MODULE_NAME];

export const selectIsInitialized = createSelector(getState, state => state.initialized);

/** The COMMITTED user (slice clock) — before gating a request on this, read `selectUserIsAuthenticated`'s note. */
export const selectUser = createSelector(getState, state => state.user);

export const selectUserSettings = createSelector(getState, state => state.userSettings);

export const selectUserLocales = createSelector(
    selectUserSettings,
    settings => (settings && settings.feedSettings && settings.feedSettings.locales) || [],
);

export const selectAuthInProgress = createSelector(getState, state => state.authInProgress);

export const selectAuthLastError = createSelector(getState, state => state.lastAuthError);

/**
 * Whether the app is COMMITTED to an authenticated user — the slice clock. This is the right read for anything
 * that should reflect the committed session the user is being shown: UI, navigation, and the sagas or listener
 * effects that follow it.
 *
 * NOT a request gate. The slice lags the auth token by the `loadUserInfo` round trips on every transition
 * (and indefinitely on commit-less ones), so a request issued in that window carries credentials this
 * selector does not describe yet. Anything deciding what a REQUEST fetches or caches gates on the token
 * clock instead: inside an RTK Query `queryFn`, `identityScoped`'s `api.identity.isAuthenticated`
 * (`@pressreader/appstore`); outside one, `svcAuth.identityKey() !== ANONYMOUS_IDENTITY_KEY`
 * (`@pressreader/services`). See the RTKQ guide's identity section.
 */
export const selectUserIsAuthenticated = createSelector(selectUser, user => Boolean(user?.isAuthenticated));

export const selectLastRecoveryEmailSentTo = createSelector(getState, state => state.lastRecoveryEmailSentTo);

export const selectMaxNumberOfEmailsReached = createSelector(getState, state => state.maxNumberOfEmailsReached);

export const selectEmailVerified = createSelector(getState, state => state.emailVerified);

export const selectLibraryPostAuthOptions = createSelector(getState, state => state.libraryPostAuthOptions);

export const selectAuthProcessState = createSelector(selectLibraryPostAuthOptions, postAuthOptions =>
    postAuthOptions?.requireAccountCreation ? postAuthOptions?.authProcessState : undefined,
);
